import { Box, Card, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { autovioColors } from "../../misc/backofficeTheme";
import { TheoryExamSimulationResult } from "../../providers/theoryExamSimulationResultsProvider";
import { useRecharts, CustomLabelProps } from "../../hooks/useRecharts";

const errorPointsCap = 20; // <-- don't show bars higher than 20 error points

type DataPoint = {
  green: number;
  red: number;
} & ({ timestamp: DateTime; errorPoints: number; noData?: never } | { noData: true });

/**
 * To give the bar either green or red color, we use a stacked bar char:
 * - if the simulation result is passed, the property `green` of the DataPoint
 *   is set to the number of error points and the property `red` is set to 0
 *   => this will show a green bar,
 * - if the simulation result is failed, the property `green` of the DataPoint
 *   is set to 0 and the property `red` is set to the number of error points
 *   => this will show a red bar,
 */
export function TheoryExamSimulationsChart({
  chartItemCount,
  data,
}: {
  chartItemCount: number;
  data: Array<TheoryExamSimulationResult>;
}) {
  const theme = useTheme();
  const dataPoints: Array<DataPoint> = data
    .sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis())
    .map(({ passed, errorPoints, timestamp }) => ({
      green: passed ? (errorPoints === 0 ? 0.3333 /* <-- to show a small green line */ : errorPoints) : 0,
      red: passed ? 0 : Math.min(errorPoints, errorPointsCap),
      timestamp,
      errorPoints,
    }));
  if (dataPoints.length < chartItemCount) {
    for (let i = dataPoints.length; i < chartItemCount; ++i) {
      dataPoints.push({ noData: true, green: 0, red: 0 });
    }
  }
  const { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } = useRecharts();
  if (!(Bar && BarChart && ResponsiveContainer && Tooltip && XAxis && YAxis)) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", height: "90px" }}>
      <ResponsiveContainer>
        <BarChart data={dataPoints} onMouseEnter={() => {}} onMouseMove={() => {}}>
          <XAxis
            dataKey="none"
            label={(props: CustomLabelProps) => {
              const { viewBox } = props;
              const { x, y } = viewBox;
              return (
                <text
                  x={x}
                  y={y + 28} // Adjust to move label below axis
                  textAnchor="start"
                  fontSize={12}
                  fill={theme.palette.text.secondary}
                >
                  Fehlerpunkte letzte Prüfungssimulationen
                </text>
              );
            }}
          />
          <Bar stackId="errorPoints" dataKey="green" fill={autovioColors.green} barSize={20} radius={[4, 4, 0, 0]} />
          <Bar stackId="errorPoints" dataKey="red" fill={autovioColors.red} barSize={20} radius={[4, 4, 0, 0]} />
          <Tooltip
            cursor={{ opacity: 0.1 }}
            content={({ active, payload }) => {
              if (!active) {
                return null;
              }
              const dataPoint = (payload as Array<{ payload: DataPoint }>)[0].payload;
              if (dataPoint.noData) {
                return null;
              }
              const { timestamp, errorPoints } = dataPoint;
              return (
                <Card
                  sx={{
                    padding: "5px",
                    borderRadius: "8px",
                    boxShadow: 1,
                    fontSize: "14px",
                  }}
                >
                  {timestamp.toFormat("dd.MM.yyyy • HH:mm")}
                  <br />
                  <div style={{ fontWeight: "600" }}>
                    {`${errorPoints}\u00a0${errorPoints === 1 ? "Fehlerpunkt" : "Fehlerpunkte"}`}
                  </div>
                </Card>
              );
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
